import React, { useState, useEffect } from 'react';
import { Search, TrendingUp, Package, AlertCircle, Users, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/modern.css';

interface ShipperSearchResult {
  shipperName: string;
  totalInspections: number;
  uniqueCarriers: number;
  avgViolations: number;
  outOfServiceRate: number;
  firstSeen: string;
  lastSeen: string;
}

const ShipperAuditPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ShipperSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [topShippers, setTopShippers] = useState<ShipperSearchResult[]>([]);
  const [loadingTop, setLoadingTop] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load top shippers on mount
  useEffect(() => {
    loadTopShippers();
  }, []);

  const loadTopShippers = async () => {
    try {
      setLoadingTop(true);
      // Search with "IN" to get top shippers (matches many company names like "INC", "INTERNATIONAL", etc.)
      const response = await api.get('/shippers/search?q=IN');
      // api.ts interceptor unwraps response.data.data to response.data
      setTopShippers(response.data || []);
    } catch (err) {
      console.error('Error loading top shippers:', err);
      setError('Failed to load top shippers');
    } finally {
      setLoadingTop(false);
    }
  };

  const handleSearch = async (query: string) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      setError(null);

      const response = await api.get(`/shippers/search?q=${encodeURIComponent(query)}`);
      // api.ts interceptor unwraps response.data.data to response.data
      setSearchResults(response.data || []);
    } catch (err) {
      console.error('Error searching shippers:', err);
      setError('Failed to search shippers');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Debounce search
    const timeoutId = setTimeout(() => {
      handleSearch(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const handleShipperClick = (shipperName: string) => {
    navigate(`/shipper-audit/${encodeURIComponent(shipperName)}`);
  };

  const getRiskLevel = (oosRate: number): { label: string; color: string } => {
    if (oosRate < 10) return { label: 'LOW', color: '#5d7148' };
    if (oosRate < 15) return { label: 'MEDIUM', color: '#b07d2b' };
    return { label: 'HIGH', color: '#b3412c' };
  };

  const displayResults = searchQuery.trim().length >= 2 ? searchResults : topShippers;

  return (
    <div className="min-h-screen bg-gray-50">
      <div style={{ padding: 'var(--space-xl)', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 'var(--space-xl)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', marginBottom: 'var(--space-sm)' }}>
            <Package size={32} style={{ color: 'var(--color-primary)' }} />
            <h1 style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--color-gray-900)', margin: 0 }}>
              Shipper Audit & Intelligence
            </h1>
          </div>
          <p style={{ fontSize: '1rem', color: 'var(--color-gray-600)', margin: 0 }}>
            Analyze shipper performance, carrier networks, and inspection patterns
          </p>
        </div>

      {/* Search Bar */}
      <div className="card-modern" style={{ marginBottom: 'var(--space-xl)' }}>
        <div style={{ position: 'relative' }}>
          <Search
            size={20}
            style={{
              position: 'absolute',
              left: 'var(--space-md)',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-gray-400)'
            }}
          />
          <input
            type="text"
            placeholder="Search for shippers (e.g., FedEx, UPS, Amazon, Walmart)..."
            value={searchQuery}
            onChange={handleSearchInput}
            style={{
              width: '100%',
              padding: 'var(--space-md) var(--space-md) var(--space-md) 3rem',
              fontSize: '1rem',
              border: '2px solid var(--color-gray-200)',
              borderRadius: 'var(--radius-md)',
              outline: 'none',
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
            onBlur={(e) => e.target.style.borderColor = 'var(--color-gray-200)'}
          />
          {isSearching && (
            <div style={{
              position: 'absolute',
              right: 'var(--space-md)',
              top: '50%',
              transform: 'translateY(-50%)'
            }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid var(--color-gray-200)',
                borderTop: '2px solid var(--color-primary)',
                borderRadius: '50%',
                animation: 'spin 0.6s linear infinite'
              }} />
            </div>
          )}
        </div>
        <div style={{
          marginTop: 'var(--space-sm)',
          padding: 'var(--space-sm) var(--space-md)',
          background: '#efebe1',
          border: '1px solid #23211d',
          borderRadius: 'var(--radius-sm)',
          fontSize: '0.875rem',
          color: '#34312c'
        }}>
          💡 <strong>Smart Merging:</strong> Similar names (e.g., "USPS", "US MAIL", "USPS Dallas") are automatically merged when you view shipper details
        </div>
        {searchQuery.trim().length > 0 && searchQuery.trim().length < 2 && (
          <div style={{
            marginTop: 'var(--space-sm)',
            fontSize: '0.875rem',
            color: 'var(--color-gray-600)'
          }}>
            Type at least 2 characters to search
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 'var(--space-lg)',
        marginBottom: 'var(--space-xl)'
      }}>
        <div className="card-modern" style={{ background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-400)', color: 'var(--color-gray-900)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <Package size={40} />
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>
                5,526,904
              </div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Inspections with Shippers</div>
            </div>
          </div>
        </div>

        <div className="card-modern" style={{ background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-400)', color: 'var(--color-gray-900)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <Users size={40} />
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>
                1,126,106
              </div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Unique Shippers</div>
            </div>
          </div>
        </div>

        <div className="card-modern" style={{ background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-400)', color: 'var(--color-gray-900)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <TrendingUp size={40} />
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>
                {topShippers.length > 0
                  ? (topShippers.reduce((sum, s) => sum + s.uniqueCarriers, 0) / topShippers.length).toFixed(0)
                  : '...'}
              </div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Avg Carriers/Shipper</div>
            </div>
          </div>
        </div>

        <div className="card-modern" style={{ background: 'var(--color-gray-100)', border: '1px solid var(--color-gray-400)', color: 'var(--color-gray-900)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <MapPin size={40} />
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700 }}>50</div>
              <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>States Covered</div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card-modern" style={{
          background: '#f2dad2',
          border: '2px solid #b3412c',
          marginBottom: 'var(--space-lg)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
            <AlertCircle size={20} style={{ color: '#b3412c' }} />
            <span style={{ color: '#b3412c', fontWeight: 600 }}>{error}</span>
          </div>
        </div>
      )}

      {/* Results Section */}
      <div className="card-modern">
        <div style={{ marginBottom: 'var(--space-lg)' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 'var(--space-xs)', color: 'var(--color-gray-900)' }}>
            {searchQuery.trim().length >= 2 ? `Search Results (${displayResults.length} shown)` : 'Top 50 Shippers by Inspection Volume'}
          </h2>
          {searchQuery.trim().length < 2 && (
            <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', margin: 0 }}>
              Showing top 50 out of 1,126,106 unique shippers in database. Search to find specific shippers.
            </p>
          )}
        </div>

        {loadingTop && !isSearching ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--color-gray-600)' }}>
            Loading shippers...
          </div>
        ) : displayResults.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--color-gray-600)' }}>
            {searchQuery.trim().length >= 2 ? 'No shippers found matching your search' : 'No shippers available'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            {displayResults.map((shipper, idx) => {
              const risk = getRiskLevel(shipper.outOfServiceRate);

              return (
                <div
                  key={idx}
                  className="card-modern"
                  onClick={() => handleShipperClick(shipper.shipperName)}
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: '2px solid var(--color-gray-200)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-primary)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-gray-200)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-gray-900)', marginBottom: '0.25rem' }}>
                        {shipper.shipperName}
                      </h3>
                      <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)' }}>
                        {shipper.uniqueCarriers} carriers • {shipper.totalInspections.toLocaleString()} inspections
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 'var(--space-lg)', flexWrap: 'wrap' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                          {shipper.avgViolations.toFixed(2)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)' }}>Avg Violations</div>
                      </div>

                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          color: risk.color
                        }}>
                          {shipper.outOfServiceRate.toFixed(1)}%
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)' }}>OOS Rate</div>
                      </div>

                      <div style={{
                        padding: '0.5rem 1rem',
                        background: risk.color,
                        color: 'white',
                        borderRadius: 'var(--radius-sm)',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {risk.label} RISK
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default ShipperAuditPage;
